import { loadModules } from "esri-loader";
import store from "../store/index";

export default class IdentifyService {
  constructor(params, kartview, selectionlayer, mapLayers) {
    // Konstruktør for IdentifyService. Initialiserer nødvendige parametre.
    this.params = params;
    this.kartview = kartview;
    this.selectionlayer = selectionlayer;
    this.mapLayers = mapLayers;
  }

  /**
   * Utfører identify-oppgave på kartet basert på hendelse og spørringsuttrykk.
   * @param {Object} event - Kart-hendelse (f.eks. klikk).
   * @param {string} queryExpression - Spørringsuttrykk for hovedlag.
   * @param {string} queryExpressionStationsFromOtherActors - Spørringsuttrykk for andre aktører.
   * @param {boolean} showStationsFromOtherActors - Om stasjoner fra andre aktører skal vises.
   * @param {boolean} useTooltip - Om tooltip skal brukes.
   * @returns {Promise} - Løser med identifiserte objekter.
   */
  executeIdentifyTask(
    event,
    queryExpression,
    queryExpressionStationsFromOtherActors,
    showStationsFromOtherActors,
    useTooltip
  ) {
    return new Promise((resolve, reject) => {
      this.params.geometry = event.mapPoint;
      this.params.mapExtent = this.kartview.extent;
      this.params.layerDefinitions = queryExpression;

      this.getIdentifyPromisesForAllActiveLayers(
        event,
        queryExpressionStationsFromOtherActors,
        showStationsFromOtherActors,
        useTooltip
      )
        .then((result) => {
          Promise.all(result.promises)
            .then((response) => {
              return this.getAllFeaturesFromIdentifyResponses(response);
            })
            .then((response) => {
              resolve({
                mapPoint: event.mapPoint,
                response: response,
                useTooltip: useTooltip,
              });
            });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Henter identify-promises for alle aktive lag i kartet.
   * @param {Object} event - Kart-hendelse.
   * @param {string} queryExpressionStationsFromOtherActors - Spørringsuttrykk for andre aktører.
   * @param {boolean} showStationsFromOtherActors - Om stasjoner fra andre aktører skal vises.
   * @param {boolean} useTooltip - Om tooltip skal brukes.
   * @returns {Promise} - Løser med promises og identifiserte lag.
   */
  getIdentifyPromisesForAllActiveLayers(
    event,
    queryExpressionStationsFromOtherActors,
    showStationsFromOtherActors,
    useTooltip
  ) {
    return new Promise((resolve, reject) => {
      loadModules([
        "esri/tasks/IdentifyTask",
        "esri/tasks/support/IdentifyParameters",
        "esri/config",
      ])
        .then(([IdentifyTask, IdentifyParameters, esriConfig]) => {
          let promises = [];
          let identifyTask = new IdentifyTask(process.env.VUE_APP_BASE_URL);
          let identifyLayers = [];

          if (esriConfig.request.interceptors.length === 0) {
            esriConfig.request.interceptors.push({
              urls: process.env.VUE_APP_BASE_URL + "identify",
              before: (params) => {
                params.requestOptions.query.layerDefs =
                  '{0: "' + this.params.layerDefinitions + '"}';
              },
            });
            esriConfig.request.interceptors.push({
              urls: process.env.VUE_APP_SECOND_LAYER_URL + "identify",
              before: (params) => {
                params.requestOptions.query.layerDefs =
                  '{0: "' + queryExpressionStationsFromOtherActors + '"}';
              },
            });
          } else {
            esriConfig.request.interceptors[0].before = (params) => {
              params.requestOptions.query.layerDefs =
                '{0: "' + this.params.layerDefinitions + '"}';
            };
            esriConfig.request.interceptors[1].before = (params) => {
              params.requestOptions.query.layerDefs =
                '{0: "' + queryExpressionStationsFromOtherActors + '"}';
            };
          }

          promises.push(identifyTask.execute(this.params));

          if (!useTooltip) {
            this.kartview.map.layers.forEach((layer) => {
              if (layer.id.includes("extra") && layer.visible) {
                identifyLayers.push(layer.id);
                let identifyParams = this.getIdentifyParams(
                  new IdentifyParameters(),
                  event.mapPoint,
                  layer
                );
                let kartlagIdentifyTask = new IdentifyTask(layer.url);
                let promise = kartlagIdentifyTask.execute(identifyParams);
                promises.push(promise);
              }
            });
          }
          resolve({ promises: promises, identifyLayers: identifyLayers });
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Lager IdentifyParameters-objekt for et gitt lag og kartpunkt.
   * @param {Object} identifyParams - IdentifyParameters-objekt.
   * @param {Object} mapPoint - Kartpunkt (geometri).
   * @param {Object} layer - Kartlag.
   * @returns {Object} - Oppdatert IdentifyParameters.
   */
  getIdentifyParams(identifyParams, mapPoint, layer) {
    identifyParams.tolerance = 10;
    identifyParams.geometry = mapPoint;
    identifyParams.mapExtent = this.kartview.extent;
    identifyParams.layerIds = layer.identifyLayers;
    identifyParams.layerOption = "visible";
    identifyParams.returnGeometry = true;
    identifyParams.returnFieldName = true;
    identifyParams.width = this.kartview.width;
    identifyParams.height = this.kartview.height;
    return identifyParams;
  }

  /**
   * Henter ut alle features fra identify-responsene.
   * @param {Array} response - Liste med identify-responser.
   * @returns {Array} - Liste med features.
   */
  getAllFeaturesFromIdentifyResponses(response) {
    let features = [];
    for (let i = 0; i < response.length; i++) {
      let layerResponse = response[i];

      if (i === 0) {
        var results = layerResponse.results;

        features = results.map((result) => {
          return this.getTidsserieFeatureWithSymbolAndPopupTemplate(result);
        });
      } else {
        layerResponse.results.forEach((result) => {
          let feature = this.getTidsserieFeatureWithSymbolAndPopupTemplate(
            result
          );
          if (feature) {
            features.push(feature);
          }
        });
      }
    }
    
    // Normaliser attributtnavnene fra lowercase til camelCase
    features.forEach(feature => {
      if (feature && feature.attributes) {
        feature.attributes = this.normalizeAttributes(feature.attributes);
      }
    });
    
    return features;
  }

  /**
   * Normaliserer attributtnavnene fra lowercase til camelCase for bakoverkompatibilitet
   * @param {Object} attributes - Attributter fra karttjenesten
   * @returns {Object} - Normaliserte attributter
   */
  normalizeAttributes(attributes) {
    // Sjekk om vi har lowercase-feltnavn (ny karttjeneste) eller uppercase (gammel tjeneste)
    // Hvis det finnes 'stasjonnr' (lowercase), normaliser. Hvis 'STASJONID' (uppercase), behold som er.
    const hasLowercaseFields = attributes.hasOwnProperty('stasjonnr') || 
                                attributes.hasOwnProperty('stasjonparameter') ||
                                attributes.hasOwnProperty('objectid');
    
    // Hvis gammel tjeneste (uppercase), returner uendret
    if (!hasLowercaseFields) {
      return attributes;
    }

    // Ny karttjeneste - normaliser fra lowercase til camelCase
    const fieldMapping = {
      'objectid': 'OBJECTID',
      'objekttype': 'objektType',
      'compoundkey12': 'compoundKey12',
      'compoundkey20': 'compoundKey20',
      'stasjonnr': 'stasjonNr',
      'stasjonnavn': 'stasjonNavn',
      'stasjonparameter': 'stasjonParameter',
      'stasjonparameterkode': 'stasjonParameterKode',
      'stasjontype': 'stasjonType',
      'stasjonhoyde_moh': 'stasjonHoyde_moh',
      'stasjonstatus': 'stasjonStatus',
      'stasjoneier': 'stasjonEier',
      'startdato': 'startDato',
      'sluttdato': 'sluttDato',
      'datafradato': 'dataFraDato',
      'datatildato': 'dataTilDato',
      'findatafradato': 'findataFraDato',
      'vassdragsnr': 'vassdragsNr',
      'vassdragsomradenr': 'vassdragsOmradeNr',
      'vassdragsomrnavn': 'vassdragsOmrNavn',
      'elvenavnhierarki': 'elvenavnHierarki',
      'observasjonssted': 'observasjonsSted',
      'feltnr': 'feltNr',
      'kdbnr': 'kdbNr',
      'minstevannforing': 'minsteVannforing',
      'hhomr': 'HHOmr',
      'reguleringstype': 'reguleringsType',
      'forsteaarutbygd': 'forsteAarUtbygd',
      'globalid': 'GlobalID',
      'normal_arsavrenning_91_20': 'normalAvrenningLsKm',
      'versjon': 'versjon',
      'status': 'status',
      'sanntid': 'sanntid',
      'gruppe': 'gruppe'
    };

    const normalizedAttrs = {};
    for (const key in attributes) {
      const mappedKey = fieldMapping[key.toLowerCase()] || key;
      normalizedAttrs[mappedKey] = attributes[key];
    }
    return normalizedAttrs;
  }

  /**
   * Henter feature fra andre lag med symbol og popup-template.
   * @param {Object} result - Resultat fra identify.
   * @param {Array} identifyLayers - Liste med identifiserte lag.
   * @param {number} index - Indeks for laget.
   * @returns {Object|null} - Feature eller null.
   */
  getOtherFeatureWithSymbolAndPopupTemplate(result, identifyLayers, index) {
    let kartlag = this.mapLayers.filter((lag) => {
      return lag.id === identifyLayers[index - 1];
    })[0];
    let currentSublayer = kartlag.sublayers.find((sublayer) => {
      return sublayer.id === result.layerId;
    });
    if (currentSublayer) {
      return result.feature;
    } else {
      return null;
    }
  }

  /**
   * Henter tidsserie-feature med symbol og popup-template.
   * @param {Object} featureData - Data for feature.
   * @returns {Object} - Feature med symbol og attributter.
   */
  getTidsserieFeatureWithSymbolAndPopupTemplate(featureData) {
    let symbolSize = store.state.mainLayerSymbolSettings.size.value;
    featureData.feature.symbol = {
      type: "simple-marker",
      outline: {
        style: "solid",
        color: [255, 0, 0, 1.0],
        width: "3px",
      },
      size: symbolSize,
      xoffset: 1,
      color: [0, 197, 255, 0.0],
    };

    var feature = featureData.feature;
    var layerName = featureData.layerName;
    feature.attributes.layerName = layerName;

    if (layerName === "tidsserier") {
      feature.attributes.isNveTimeseries = true;
    }
    return feature;
  }

  /**
   * Lager popup-innhold for en gitt stasjon.
   * @param {string} stasjonNr - Stasjonsnummer.
   * @param {string} stasjonParameterKode - Parameterkode.
   * @param {string} versjon - Versjon.
   * @returns {HTMLElement} - Div-element for popup.
   */
  getPopupContent(stasjonNr, stasjonParameterKode, versjon) {
    var popupDiv = document.createElement("div");
    popupDiv.id =
      "popupContent" +
      stasjonNr.split(".").join("") +
      stasjonParameterKode +
      versjon;
    return popupDiv;
  }
}
